import { useDebouncedEffect } from '@/app/hooks/useDebouncedEffect';
import { usePostCreatePaymentIntent } from '@/src/api/client';
import { InitPaymentIntentRequestPaymentMethod } from '@/src/api/models';
import { captureException } from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

type Params = {
  projectId: string;
  totalAmountInCents: number;
  language: string;
  isFormValid: boolean;
};

export const useCreatePaymentIntent = ({ projectId, totalAmountInCents, language, isFormValid }: Params) => {
  const createPaymentIntent = usePostCreatePaymentIntent().mutateAsync;

  const canFetchData = isFormValid && !!projectId && totalAmountInCents > 0;

  // 1️⃣ Initial static request – only when form is not valid
  const {
    data: initialIntent,
    isPending: isLoadingInitial,
    error: initialError,
  } = useQuery({
    queryKey: ['initial-payment-intent'],
    queryFn: () =>
      createPaymentIntent({
        data: {
          paymentMethod: InitPaymentIntentRequestPaymentMethod.applepay,
          project: projectId,
          totalAmountInCents,
        },
      }),
    enabled: !canFetchData,
    staleTime: Infinity,
  });

  // 2️⃣ Live query – key is debounced, so query fires after delay
  const [liveQueryKey, setLiveQueryKey] = useState<string | null>(null);

  useDebouncedEffect(
    () => {
      if (canFetchData) {
        // important: stringify to ensure change detection
        const key = JSON.stringify({
          projectId,
          totalAmountInCents,
          language,
          date: new Date().toISOString(),
        });

        setLiveQueryKey(key);
      }
    },
    [projectId, totalAmountInCents, language, canFetchData],
    250,
  );

  const {
    data: liveIntent,
    isPending: isLoadingLive,
    error: liveError,
  } = useQuery({
    queryKey: liveQueryKey ? ['live-payment-intent', liveQueryKey] : [],
    queryFn: () =>
      createPaymentIntent({
        data: {
          paymentMethod: InitPaymentIntentRequestPaymentMethod.applepay,
          project: projectId,
          totalAmountInCents,
        },
      }),
    enabled: !!liveQueryKey,
    staleTime: 0,
    retry: false,
  });

  const paymentIntentError = liveError ?? initialError;

  useEffect(() => {
    if (paymentIntentError) {
      captureException(paymentIntentError, { extra: { function: 'postCreatePaymentIntent' } });
    }
  }, [paymentIntentError]);

  const paymentIntent = liveIntent ?? initialIntent;
  const isLoadingPaymentIntent = isLoadingInitial || (canFetchData && isLoadingLive);

  return { paymentIntent, isLoadingPaymentIntent, paymentIntentError };
};
